'use client';

import { useCommendaOSList } from '@commenda/api/commenda-os-dsl';
import { LoadingIndicator, MultiSelect } from '@commenda/kakashi';
import type { CorporationType, Country } from '@prisma/client';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FormContext } from '../../../hooks/FormContext';
import { isRuleActive } from '../../../utils/validation';
import { LabelWithGuidanceTooltip } from '../LabelWithGuidanceTooltip';
import { BusinessEntityAdditionalFields } from './BusinessEntityAdditionalFields';
import { BusinessEntityCard } from './BusinessEntityCard';
import { BusinessEntityCreationFields } from './BusinessEntityCreationFields';
import { useBusinessEntityCreation } from './hooks/useBusinessEntityCreation';
import {
  BusinessEntityFormData,
  BusinessEntitySelectOrCreateProps,
  SelectedBusinessEntity,
} from './types';

/**
 * BusinessEntitySelectOrCreate Component
 *
 * A specialized component for selecting existing or creating new business entities.
 * Unlike the generic EntitySelectOrCreate, this component has:
 * - Fixed required fields: Legal Name, Country, Jurisdiction, Entity Type
 * - Cascading dropdowns: Jurisdiction depends on Country, Entity Type depends on both
 * - Country selector with flags and priority countries (US, India, etc.)
 * - Optional extra fields configured by form creator
 *
 * Key features:
 * - Multi-select dropdown with "Add new" option
 * - Selected entities displayed as cards
 * - Inline expansion for editing entity fields
 * - Cascading dropdown logic with auto-select
 * - Support for creating new entities with proper validation
 */
export const BusinessEntitySelectOrCreateComponent = ({
  fieldId,
  label,
  description,
  placeholder,
  guidance,
  validation,
  props = {},
  template = [],
}: BusinessEntitySelectOrCreateProps) => {
  const {
    formData,
    updateField,
    errors,
    validateField,
    isFieldVisible,
    isFieldDisabled,
    dslContext,
  } = useContext(FormContext);

  const {
    displayField = 'generalCorporationInfo.legalName',
    valueField = 'id',
    listPath,
    minSelection = 1,
    maxSelection = 1,
    addNewLabel,
    selectLabel,
    disabled: propsDisabled = false,
  } = props;

  // State for expanded entities (showing inline fields)
  const [expandedEntityIds, setExpandedEntityIds] = useState<Set<string>>(
    new Set()
  );

  // State for new entity counter (for generating unique IDs)
  const [newEntityCounter, setNewEntityCounter] = useState(0);

  const handleEntityInfoChange = useCallback(
    (entityId: string, data: BusinessEntityFormData) => {
      if (!entityId.startsWith('new-')) return;
      updateField(`${fieldId}-entity-${entityId}-data`, data);
    },
    [fieldId, updateField]
  );

  // Business entity creation state management
  const {
    cascading,
    getEntityFormData,
    updateEntityFormData,
    validateEntityFormData,
    isEntityInfoFilled,
    getEntityDisplayName,
  } = useBusinessEntityCreation({
    parentFieldId: fieldId,
    onEntityInfoChange: handleEntityInfoChange,
  });

  // Fetch entities from Commenda OS
  const {
    data: entities,
    isLoading,
    error,
  } = useCommendaOSList<Record<string, unknown>>(
    'corporation',
    dslContext as { companyId: number } | undefined,
    listPath
  );

  // Get selected entity IDs from form data
  const selectedEntityIds = useMemo(() => {
    const value = formData[fieldId];
    if (Array.isArray(value)) return value as string[];
    if (value) return [String(value)];
    return [];
  }, [formData, fieldId]);

  // Helper to get nested value from object
  const getNestedValue = useCallback(
    (obj: Record<string, unknown>, path: string): unknown => {
      return path.split('.').reduce((current: unknown, key: string) => {
        if (current && typeof current === 'object') {
          return (current as Record<string, unknown>)[key];
        }
        return undefined;
      }, obj);
    },
    []
  );

  // Get required field data for existing entities from the fetched entity list
  const getExistingEntityFormData = useCallback(
    (entityId: string): BusinessEntityFormData => {
      const entity = entities?.find(
        (e) => String(getNestedValue(e, valueField)) === entityId
      );
      if (!entity) {
        return {
          legalName: '',
          incorporationCountry: null,
          jurisdiction: null,
          businessEntityType: null,
        };
      }

      return {
        legalName:
          (getNestedValue(
            entity,
            'generalCorporationInfo.legalName'
          ) as string) || '',
        incorporationCountry:
          (getNestedValue(
            entity,
            'generalCorporationInfo.incorporationCountry'
          ) as Country) || null,
        jurisdiction:
          (getNestedValue(
            entity,
            'generalCorporationInfo.jurisdiction'
          ) as string) || null,
        businessEntityType:
          (getNestedValue(
            entity,
            'generalCorporationInfo.businessEntityType'
          ) as CorporationType) || null,
      };
    },
    [entities, valueField, getNestedValue]
  );

  // Transform entities into select options
  const options = useMemo(() => {
    if (!entities || !Array.isArray(entities)) return [];

    return entities.map((entityItem) => {
      const displayValue = getNestedValue(entityItem, displayField);
      const idValue = getNestedValue(entityItem, valueField);

      return {
        value: String(idValue ?? ''),
        label: String(displayValue ?? `Corporation ${idValue}`),
      };
    });
  }, [entities, displayField, valueField, getNestedValue]);

  // Check if an entity has missing required fields
  const checkEntityMissingFields = useCallback(
    (entityId: string, isNew: boolean): boolean => {
      if (isNew) {
        // For new entities, check if all required fields are filled
        return !isEntityInfoFilled(entityId);
      }

      // For existing entities, check if required fields are populated
      const entityData = getExistingEntityFormData(entityId);
      return (
        !entityData.legalName ||
        !entityData.incorporationCountry ||
        !entityData.jurisdiction ||
        !entityData.businessEntityType
      );
    },
    [isEntityInfoFilled, getExistingEntityFormData]
  );

  // Build selected entities with display names and status
  const selectedEntities: SelectedBusinessEntity[] = useMemo(() => {
    return selectedEntityIds.map((id) => {
      const isNew = id.startsWith('new-');
      const hasMissingFields = checkEntityMissingFields(id, isNew);

      if (isNew) {
        // For new entities, get display name from form data
        const displayName = getEntityDisplayName(id);
        const creationData = getEntityFormData(id);

        return {
          id,
          displayName,
          isNew: true,
          hasMissingFields,
          creationData,
        };
      }

      // For existing entities, find the display name from options
      const option = options.find((opt) => opt.value === id);
      return {
        id,
        displayName: option?.label || `Corporation ${id}`,
        isNew: false,
        hasMissingFields,
      };
    });
  }, [
    selectedEntityIds,
    options,
    checkEntityMissingFields,
    getEntityDisplayName,
    getEntityFormData,
  ]);

  // Check if any entity has missing extra fields (from template)
  const checkEntityMissingAdditionalFields = useCallback(
    (entityId: string, isNew: boolean): boolean => {
      if (template.length === 0) return false;

      return template.some((field) => {
        const entityFieldId = `${fieldId}-entity-${entityId}-${field.fieldId || field.id}`;
        const isRequired = field.validation?.rules?.required;
        const isRequiredEnabled =
          typeof isRequired === 'object' ? isRequired.enabled : !!isRequired;

        if (!isRequiredEnabled) return false;

        // Check form data first (for user-entered values)
        const formValue = formData[entityFieldId];
        if (formValue !== undefined && formValue !== null && formValue !== '') {
          return false; // Has a value in form data
        }

        // For existing entities, also check the entity's actual data
        if (!isNew) {
          const entity = entities?.find(
            (e) => String(getNestedValue(e, valueField)) === entityId
          );
          if (entity) {
            // Get the field path from the data source
            const readPath = field.dataSource?.commendaOS?.field?.read;
            if (readPath) {
              const entityValue = getNestedValue(entity, readPath);
              if (
                entityValue !== undefined &&
                entityValue !== null &&
                entityValue !== ''
              ) {
                return false; // Entity has the value
              }
            }
          }
        }

        return true; // Field is required and has no value
      });
    },
    [template, fieldId, formData, entities, valueField, getNestedValue]
  );

  // Check if any entity has missing fields (required or extra)
  const hasEntitiesWithMissingFields = useMemo(() => {
    return selectedEntities.some(
      (e) =>
        e.hasMissingFields || checkEntityMissingAdditionalFields(e.id, e.isNew)
    );
  }, [selectedEntities, checkEntityMissingAdditionalFields]);

  const disabled = propsDisabled || isFieldDisabled(fieldId);
  const isRequired = isRuleActive(validation?.rules?.required, isFieldVisible);

  // Build effective validation rules including min/max selection
  const effectiveValidationRules = useMemo(() => {
    const rules = { ...validation?.rules };

    if (minSelection > 0) {
      rules.minLength = {
        enabled: true,
        value: minSelection,
        message: `Please select at least ${minSelection} business entit${minSelection > 1 ? 'ies' : 'y'}`,
      };
      rules.required = {
        enabled: true,
        message: `Please select at least ${minSelection} business entit${minSelection > 1 ? 'ies' : 'y'}`,
      };
    }

    if (maxSelection > 0) {
      rules.maxLength = {
        enabled: true,
        value: maxSelection,
        message: `Please select no more than ${maxSelection} business entit${maxSelection > 1 ? 'ies' : 'y'}`,
      };
    }

    return rules;
  }, [validation?.rules, minSelection, maxSelection]);

  // Set error when there are entities with missing fields, clear when all complete
  useEffect(() => {
    if (hasEntitiesWithMissingFields) {
      validateField(fieldId, null, {
        required: {
          enabled: true,
          message:
            'Please fill in the required fields for all business entities',
        },
      });
    } else if (selectedEntityIds.length > 0) {
      // Clear the error when all entities are complete
      // Re-validate with the actual values to clear the error
      validateField(fieldId, selectedEntityIds, effectiveValidationRules);
    }
  }, [
    hasEntitiesWithMissingFields,
    fieldId,
    validateField,
    selectedEntityIds,
    effectiveValidationRules,
  ]);

  // Store new entity form data in the main form data for submission
  useEffect(() => {
    selectedEntities.forEach((entity) => {
      if (entity.isNew && entity.creationData) {
        // Store the creation data in form data for submission
        const dataKey = `${fieldId}-entity-${entity.id}-data`;
        const currentData = formData[dataKey];

        // Only update if data has changed
        if (
          JSON.stringify(currentData) !== JSON.stringify(entity.creationData)
        ) {
          updateField(dataKey, entity.creationData);
        }
      }
    });
  }, [selectedEntities, fieldId, formData, updateField]);

  // Handle selection changes from MultiSelect
  const handleSelectionChange = useCallback(
    (newValues: string[]) => {
      updateField(fieldId, newValues);
      validateField(fieldId, newValues, effectiveValidationRules);
    },
    [fieldId, updateField, validateField, effectiveValidationRules]
  );

  // Handle adding a new entity
  const handleAddNew = useCallback(() => {
    const newId = `new-${Date.now()}-${newEntityCounter}`;
    setNewEntityCounter((prev) => prev + 1);

    const newSelection = [...selectedEntityIds, newId];
    updateField(fieldId, newSelection);

    // Auto-expand the new entity for editing
    setExpandedEntityIds((prev) => new Set([...Array.from(prev), newId]));
  }, [selectedEntityIds, fieldId, updateField, newEntityCounter]);

  // Handle removing an entity
  const handleRemoveEntity = useCallback(
    (entityId: string) => {
      const newSelection = selectedEntityIds.filter((id) => id !== entityId);
      updateField(fieldId, newSelection);

      // Remove from expanded set
      setExpandedEntityIds((prev) => {
        const next = new Set(prev);
        next.delete(entityId);
        return next;
      });
    },
    [selectedEntityIds, fieldId, updateField]
  );

  // Handle toggling entity expansion
  const handleToggleExpand = useCallback((entityId: string) => {
    setExpandedEntityIds((prev) => {
      const next = new Set(prev);
      if (next.has(entityId)) {
        next.delete(entityId);
      } else {
        next.add(entityId);
      }
      return next;
    });
  }, []);

  // Handle form data change for a new entity
  const handleEntityFormDataChange = useCallback(
    (entityId: string, data: Partial<BusinessEntityFormData>) => {
      updateEntityFormData(entityId, data);
    },
    [updateEntityFormData]
  );

  // Check if we can add more entities
  const canAddMore =
    maxSelection === 0 || selectedEntityIds.length < maxSelection;

  // Show loading state
  if (isLoading || cascading.isLoading) {
    return (
      <div className="mb-4" data-field-id={fieldId}>
        {(label || description) && (
          <LabelWithGuidanceTooltip
            label={label}
            guidance={guidance?.text}
            isRequired={isRequired}
            description={description}
          />
        )}
        <div className="flex items-center justify-center p-4 border rounded-md bg-bg-secondary">
          <LoadingIndicator size={16} />
          <span className="ml-2 text-sm text-text-tertiary">
            Loading business entities...
          </span>
        </div>
      </div>
    );
  }

  // Show error state
  if (error || cascading.error) {
    return (
      <div className="mb-4" data-field-id={fieldId}>
        {(label || description) && (
          <LabelWithGuidanceTooltip
            label={label}
            guidance={guidance?.text}
            isRequired={isRequired}
            description={description}
          />
        )}
        <div className="p-4 border border-border-error rounded-md bg-bg-error-subtle">
          <p className="text-sm text-text-error">
            Failed to load business entities. Please try again.
          </p>
        </div>
      </div>
    );
  }

  // Show empty state if no context
  if (!dslContext?.companyId) {
    return (
      <div className="mb-4" data-field-id={fieldId}>
        {(label || description) && (
          <LabelWithGuidanceTooltip
            label={label}
            guidance={guidance?.text}
            isRequired={isRequired}
            description={description}
          />
        )}
        <div className="p-4 border border-border-warning rounded-md bg-bg-warning-subtle">
          <p className="text-sm text-text-warning">
            Company context required to load business entities.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4" data-field-id={fieldId}>
      {(label || description) && (
        <LabelWithGuidanceTooltip
          label={label}
          guidance={guidance?.text}
          isRequired={isRequired}
          description={description}
        />
      )}

      {/* MultiSelect Dropdown */}
      <MultiSelect
        id={`${fieldId}-multiselect`}
        options={options}
        onValueChange={handleSelectionChange}
        defaultValue={selectedEntityIds}
        placeholder={
          selectLabel || placeholder || 'Add or select business entities'
        }
        hideSelectAll
        hideTags
        disabled={disabled}
        error={errors[fieldId]}
        maxSelectable={maxSelection === 0 ? null : maxSelection}
        addItemConfig={
          canAddMore
            ? {
                shouldAllowAdd: true,
                onAddItem: handleAddNew,
                placeholder: addNewLabel || 'Add new business entity',
              }
            : undefined
        }
      />

      {/* Selected Entities List */}
      {selectedEntities.length > 0 && (
        <div className="mt-3 space-y-2">
          {selectedEntities.map((selectedEntity) => {
            const isExpanded = expandedEntityIds.has(selectedEntity.id);

            return (
              <div key={selectedEntity.id}>
                <BusinessEntityCard
                  entity={selectedEntity}
                  onRemove={handleRemoveEntity}
                  onToggleExpand={handleToggleExpand}
                  isExpanded={isExpanded}
                  disabled={disabled}
                />

                {/* Inline Fields Container - shown when expanded */}
                {isExpanded && (
                  <div className="border border-t-0 border-border-primary rounded-b-md p-4 bg-bg-secondary-subtle">
                    {selectedEntity.isNew ? (
                      <>
                        {/* Required fields for new entities */}
                        <BusinessEntityCreationFields
                          entityId={selectedEntity.id}
                          parentFieldId={fieldId}
                          formData={getEntityFormData(selectedEntity.id)}
                          onChange={(data) =>
                            handleEntityFormDataChange(selectedEntity.id, data)
                          }
                          disabled={disabled}
                          errors={validateEntityFormData(selectedEntity.id)}
                        />

                        {/* Extra fields from template */}
                        {template.length > 0 && (
                          <BusinessEntityAdditionalFields
                            parentFieldId={fieldId}
                            entityId={selectedEntity.id}
                            isNewEntity
                            template={template}
                            entities={entities}
                            valueField={valueField}
                          />
                        )}

                        <p className="mt-4 text-xs text-text-tertiary">
                          Fill in the required fields to create a new business
                          entity.
                        </p>
                      </>
                    ) : (
                      <>
                        {/* Required fields for existing entities - read-only */}
                        <BusinessEntityCreationFields
                          entityId={selectedEntity.id}
                          parentFieldId={fieldId}
                          formData={getExistingEntityFormData(
                            selectedEntity.id
                          )}
                          onChange={() => {}} // No-op for existing entities
                          disabled // Always disabled for existing entities
                          errors={{}}
                        />

                        {/* Extra fields from template */}
                        {template.length > 0 && (
                          <BusinessEntityAdditionalFields
                            parentFieldId={fieldId}
                            entityId={selectedEntity.id}
                            isNewEntity={false}
                            template={template}
                            entities={entities}
                            valueField={valueField}
                          />
                        )}

                        {template.length === 0 && (
                          <p className="mt-4 text-sm text-text-tertiary">
                            No additional fields configured for this entity.
                          </p>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Validation Error */}
      {errors[fieldId] && (
        <p className="mt-1 text-sm text-text-error">{errors[fieldId]}</p>
      )}

      {/* Selection count hint */}
      {(minSelection > 0 || maxSelection > 0) && (
        <p className="mt-1 text-xs text-text-tertiary">
          {minSelection > 0 && maxSelection > 0 && minSelection === maxSelection
            ? `Select exactly ${minSelection} business entit${minSelection > 1 ? 'ies' : 'y'}`
            : minSelection > 0 && maxSelection > 0
              ? `Select ${minSelection} to ${maxSelection} business entities`
              : minSelection > 0
                ? `Select at least ${minSelection} business entit${minSelection > 1 ? 'ies' : 'y'}`
                : maxSelection > 0
                  ? `Select up to ${maxSelection} business entit${maxSelection > 1 ? 'ies' : 'y'}`
                  : null}
        </p>
      )}
    </div>
  );
};
